import { ip_back } from "../config";
import { serveBallWindowsFromEvents } from "../utils/serveHeuristics";

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function parseJson(resp) {
  try {
    return await resp.json();
  } catch {
    return {};
  }
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener?.(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

function isNetworkFetchError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return (
    err instanceof TypeError ||
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  );
}

function formatFetchError(err, context) {
  if (isNetworkFetchError(err)) {
    return (
      `Lost connection to the ball server while ${context}. ` +
      "The job may still be running — wait a moment and try again."
    );
  }
  return String(err?.message || err || "Ball request failed");
}

async function fetchJson(url, options = {}, { retries = 3, retryDelayMs = 1500 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    try {
      const resp = await fetch(url, options);
      const json = await parseJson(resp);
      return { resp, json };
    } catch (err) {
      lastErr = err;
      if (err?.name === "AbortError") throw err;
      if (!isNetworkFetchError(err) || attempt >= retries) throw err;
      await delay(retryDelayMs * (attempt + 1), options.signal);
    }
  }
  throw lastErr;
}

function appendClipParams(q, { videoUrl, sequenceOrder } = {}) {
  if (videoUrl) q.set("video_url", String(videoUrl));
  const seq = Number(sequenceOrder);
  if (Number.isFinite(seq) && seq > 0) {
    q.set("sequence_order", String(Math.floor(seq)));
  }
  return q;
}

function listStrokeItems(block, detectionId) {
  if (!block || typeof block !== "object") return [];
  if (detectionId === "serve_toss") {
    return Array.isArray(block.events) ? block.events : [];
  }
  if (Array.isArray(block.strokes) && block.strokes.length) return block.strokes;
  return Array.isArray(block.events) ? block.events : [];
}

/** Pose stroke contact times (ms) used as a fallback HUD count. */
export function poseStrokeContactMs(poseResult) {
  const detections = poseResult?.summary?.detections;
  if (!detections || typeof detections !== "object") return [];
  const times = [];
  for (const [id, block] of Object.entries(detections)) {
    const items = listStrokeItems(block, id);
    for (const stroke of items) {
      const t = Number(stroke?.t_contact_ms ?? stroke?.t_peak_ms ?? stroke?.t_ms);
      if (Number.isFinite(t) && t >= 0) times.push(Math.round(t));
    }
  }
  return [...new Set(times)].sort((a, b) => a - b);
}

const BALL_STROKE_KIND_IDS = {
  all: ["fh_topspin_left", "fh_left", "bh_topspin_left", "bh_left", "serve_toss"],
  ts: ["fh_topspin_left"],
  fh: ["fh_left"],
  bh: ["bh_topspin_left"],
  bhd: ["bh_left"],
  srv: ["serve_toss"],
  serve: ["serve_toss"],
  serve_toss: ["serve_toss"],
};

export const BALL_STROKE_KINDS = [
  { id: "all", labelKey: "poseStrokeKindAll", label: "All strokes" },
  { id: "ts", labelKey: "poseFhTopspinShortLabel", label: "FH topspin" },
  { id: "fh", labelKey: "poseFhShortLabel", label: "Forehand" },
  { id: "bh", labelKey: "poseBhTopspinShortLabel", label: "BH topspin" },
  { id: "bhd", labelKey: "poseBhShortLabel", label: "Backhand" },
  { id: "srv", labelKey: "poseServeShortLabel", label: "Serve" },
];

export function normalizeBallStrokeKind(raw) {
  const k = String(raw || "all").toLowerCase().trim();
  return BALL_STROKE_KIND_IDS[k] ? k : "all";
}

export function isServeBallStrokeKind(raw) {
  return normalizeBallStrokeKind(raw) === "srv";
}

/**
 * Which player boxes ball detection needs.
 * Serve: only the server (A/left unless tosses are on the right). Other strokes: both.
 */
export function ballPlayerBoxesMode(
  strokeKind,
  serveDetection = null,
  poseDetectionPlayer = "A"
) {
  if (!isServeBallStrokeKind(strokeKind)) return "Both";
  const events = Array.isArray(serveDetection?.events) ? serveDetection.events : [];
  if (events.length) {
    let a = false;
    let b = false;
    for (const event of events) {
      if (event?.player === "B" || event?.server_side === "right") b = true;
      else a = true;
    }
    if (a && b) return "Both";
    if (b && !a) return "B";
    return "A";
  }
  return String(poseDetectionPlayer || "A") === "B" ? "B" : "A";
}

export function ballPlayerRoisReady(mode, left, right) {
  if (mode === "B") return Boolean(right);
  if (mode === "Both") return Boolean(left && right);
  return Boolean(left);
}

function isServeItem(item, detectionId) {
  if (detectionId === "serve_toss") return true;
  if (!item || typeof item !== "object") return false;
  if (item.toss_hand || item.source === "trajectory") return true;
  const phases = item.phases;
  return Boolean(phases?.toss || phases?.preparation);
}

function phaseWindowFromStroke(stroke, detectionId) {
  if (!stroke || typeof stroke !== "object") return null;
  if (isServeItem(stroke, detectionId)) {
    return serveBallWindowsFromEvents([stroke])[0] || null;
  }
  const phases = stroke.phases && typeof stroke.phases === "object" ? stroke.phases : {};
  const t0 = Number(
    phases.forward_swing?.t_start_ms ?? stroke.t_valley_ms ?? stroke.t_forward_ms
  );
  const t1 = Number(
    phases.recovery?.t_end_ms ?? phases.recovery?.t_start_ms ?? stroke.t_recovery_ms
  );
  if (Number.isFinite(t0) && Number.isFinite(t1) && t1 - t0 >= 80) {
    return { t0: Math.round(t0), t1: Math.round(t1) };
  }
  const contact = Number(stroke.t_contact_ms ?? stroke.t_peak_ms);
  if (!Number.isFinite(contact) || contact < 0) return null;
  const fallback0 = Number(phases.forward_swing?.t_end_ms);
  const peak = Number(stroke.t_peak_ms);
  const start = Number.isFinite(fallback0) ? fallback0 : contact - 200;
  const end = Number.isFinite(peak) ? peak + 350 : contact + 350;
  if (end - start < 80) return null;
  return { t0: Math.max(0, Math.round(start)), t1: Math.round(end) };
}

function mergeWindows(windows) {
  const sorted = (windows || [])
    .filter((w) => w && Number.isFinite(w.t0) && Number.isFinite(w.t1) && w.t1 > w.t0)
    .sort((a, b) => a.t0 - b.t0);
  if (!sorted.length) return [];
  const out = [{ t0: sorted[0].t0, t1: sorted[0].t1 }];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.t0 <= last.t1) last.t1 = Math.max(last.t1, cur.t1);
    else out.push({ t0: cur.t0, t1: cur.t1 });
  }
  return out;
}

/**
 * Stroke windows for the selected pose type.
 * FH/BH: forward swing → recovery. Serve: still-hand prep through far-side flight.
 * `detections` is poseResult.summary.detections or a {id: {strokes|events}} map.
 */
export function buildBallStrokeWindows(detections, strokeKind = "all") {
  if (!detections || typeof detections !== "object") return [];
  const kind = normalizeBallStrokeKind(strokeKind);
  const ids = BALL_STROKE_KIND_IDS[kind] || BALL_STROKE_KIND_IDS.all;
  const windows = [];
  const serveEvents = [];
  for (const id of ids) {
    const items = listStrokeItems(detections[id], id);
    if (id === "serve_toss") {
      serveEvents.push(...items);
      continue;
    }
    for (const stroke of items) {
      const win = phaseWindowFromStroke(stroke, id);
      if (win) windows.push(win);
    }
  }
  windows.push(...serveBallWindowsFromEvents(serveEvents));
  return mergeWindows(windows);
}

export function countStrokesForKind(detections, strokeKind) {
  if (!detections || typeof detections !== "object") return 0;
  const ids = BALL_STROKE_KIND_IDS[normalizeBallStrokeKind(strokeKind)] || [];
  let n = 0;
  for (const id of ids) {
    n += listStrokeItems(detections[id], id).length;
  }
  return n;
}

/**
 * Load existing complete ball result for this exercise clip (no enqueue).
 */
export async function tryLoadExistingBall(teId, detector = "tracknet", clip = {}) {
  if (!teId) return null;
  const q = appendClipParams(
    new URLSearchParams({
      te_id: teId,
      detector: detector || "tracknet",
    }),
    clip
  );
  const resp = await fetch(
    `${ip_back}api/mobileAPI/ball/results?${q.toString()}`,
    { method: "GET", headers: authHeaders() }
  );
  const json = await parseJson(resp);
  if (!resp.ok || !json?.data) return null;
  if (json.data.status !== "complete") return null;
  const result =
    json.data.result ||
    (json.data.result_json
      ? typeof json.data.result_json === "string"
        ? JSON.parse(json.data.result_json)
        : json.data.result_json
      : null);
  return result && typeof result === "object" ? result : null;
}

/**
 * Enqueue (or reuse) ball job and poll until complete.
 * onProgress({ progress, etaSeconds, status })
 */
export async function fetchOrEnqueueBallResult(
  teId,
  {
    detector = "tracknet",
    videoUrl = null,
    cornersNorm = null,
    playerRois = null,
    detectionRoi = null,
    ballDetectionFps = 60,
    sequenceOrder = null,
    poseStrokeContactsMs = null,
    strokeWindowsMs = null,
    strokeKind = "all",
    force = false,
    onProgress,
    signal,
    maxAttempts = 900,
  } = {}
) {
  if (!teId) return null;

  const body = {
    te_id: teId,
    detector: detector || "tracknet",
    video_url: videoUrl || undefined,
    sequence_order:
      Number.isFinite(Number(sequenceOrder)) && Number(sequenceOrder) > 0
        ? Math.floor(Number(sequenceOrder))
        : undefined,
  };
  if (Array.isArray(cornersNorm) && cornersNorm.length === 4) {
    body.corners_norm = cornersNorm;
  }
  if (
    playerRois &&
    typeof playerRois === "object" &&
    (playerRois.left || playerRois.right)
  ) {
    body.player_rois = {
      left: playerRois.left || null,
      right: playerRois.right || null,
    };
  }
  if (
    detectionRoi &&
    typeof detectionRoi === "object" &&
    Number.isFinite(Number(detectionRoi.x)) &&
    Number.isFinite(Number(detectionRoi.y)) &&
    Number.isFinite(Number(detectionRoi.w ?? detectionRoi.width)) &&
    Number.isFinite(Number(detectionRoi.h ?? detectionRoi.height))
  ) {
    body.detection_roi = {
      x: Number(detectionRoi.x),
      y: Number(detectionRoi.y),
      w: Number(detectionRoi.w ?? detectionRoi.width),
      h: Number(detectionRoi.h ?? detectionRoi.height),
    };
  }
  const bfps = Number(ballDetectionFps);
  if (Number.isFinite(bfps) && bfps > 0) {
    body.ball_detection_fps = bfps;
  } else {
    body.ball_detection_fps = 60;
  }
  const kind = normalizeBallStrokeKind(strokeKind);
  if (kind) body.stroke_kind = kind;
  if (Array.isArray(strokeWindowsMs) && strokeWindowsMs.length) {
    body.stroke_windows_ms = strokeWindowsMs
      .map((w) => ({
        t0: Math.round(Number(w?.t0)),
        t1: Math.round(Number(w?.t1)),
      }))
      .filter((w) => Number.isFinite(w.t0) && Number.isFinite(w.t1) && w.t1 > w.t0);
  } else if (Array.isArray(poseStrokeContactsMs) && poseStrokeContactsMs.length) {
    body.pose_stroke_contacts_ms = poseStrokeContactsMs
      .map((t) => Math.round(Number(t)))
      .filter((t) => Number.isFinite(t) && t >= 0);
  }
  if (force) body.force = true;

  let enqueueJson;
  try {
    const enqueued = await fetchJson(
      `${ip_back}api/mobileAPI/ball/jobs`,
      {
        method: "POST",
        headers: authHeaders(),
        signal,
        body: JSON.stringify(body),
      },
      { retries: 3 }
    );
    enqueueJson = enqueued.json;
    if (!enqueued.resp.ok) {
      const err = new Error(
        enqueueJson?.message ||
          `Could not start ball job (${enqueued.resp.status})`
      );
      err.code = enqueueJson?.code || null;
      throw err;
    }
  } catch (err) {
    if (
      err?.code === "TABLE_GEOMETRY_REQUIRED" ||
      err?.code === "BALL_CALIBRATION_REQUIRED"
    ) {
      throw err;
    }
    throw new Error(formatFetchError(err, "starting ball analysis"));
  }

  const jobId = enqueueJson?.data?.job_id || null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const q = appendClipParams(
      new URLSearchParams({ te_id: teId, detector: detector || "tracknet" }),
      { videoUrl, sequenceOrder }
    );
    if (jobId) q.set("job_id", jobId);
    let resp;
    let json;
    try {
      const polled = await fetchJson(
        `${ip_back}api/mobileAPI/ball/results?${q.toString()}`,
        { method: "GET", headers: authHeaders(), signal },
        { retries: 2 }
      );
      resp = polled.resp;
      json = polled.json;
    } catch (err) {
      throw new Error(formatFetchError(err, "checking ball progress"));
    }
    const data = json?.data;
    if (!data) {
      await delay(2000, signal);
      continue;
    }

    const progress = Number(data.progress) || 0;
    const etaSeconds =
      data.eta_seconds != null ? Number(data.eta_seconds) : null;
    onProgress?.({
      progress,
      etaSeconds: Number.isFinite(etaSeconds) ? etaSeconds : null,
      status: data.status,
    });

    if (data.status === "complete") {
      return (
        data.result ||
        (data.result_json
          ? typeof data.result_json === "string"
            ? JSON.parse(data.result_json)
            : data.result_json
          : null)
      );
    }
    if (data.status === "failed") {
      throw new Error(data.error_message || "Ball analysis failed");
    }
    await delay(attempt === 0 ? 500 : 2000, signal);
  }
  throw new Error(
    "Ball analysis timed out while waiting for the server. Try again later."
  );
}

/** Delete cached ball jobs for this clip (or whole te_id if no clip given). */
export async function flushBallResults(teId, clip = {}) {
  if (!teId) throw new Error("te_id required");
  const params = appendClipParams(new URLSearchParams({ te_id: teId }), clip);
  if (clip?.detector) params.set("detector", String(clip.detector));
  const resp = await fetch(
    `${ip_back}api/mobileAPI/ball/flush?${params.toString()}`,
    { method: "POST", headers: authHeaders() }
  );
  const json = await parseJson(resp);
  if (!resp.ok || json?.status === false) {
    throw new Error(json?.message || "Failed to clear ball results");
  }
  return json.data;
}
